import { useEffect, useRef } from "react";
import { useSecrets } from "@/context/SecretsContext";

const KONAMI = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a"
];

const isTyping = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

/**
 * Global keyboard / console anomalies. Only the anomalies armed for this
 * session are listening, so no two visits behave the same.
 */
export const AnomalyListeners = () => {
  const { isEggActive, markEggFound, triggerGlitch, openOverlay } = useSecrets();
  const konamiRef = useRef(0);
  const bufferRef = useRef("");
  const consoleRef = useRef(false);

  // ---- diagnostic anomaly: a warning for anyone who opens dev tools ----
  useEffect(() => {
    if (!isEggActive("console") || consoleRef.current) return;
    consoleRef.current = true;
    const head =
      "color:#f6a23c;font:600 13px 'IBM Plex Mono',monospace;letter-spacing:.14em";
    const body = "color:#8c97a6;font:400 12px 'IBM Plex Mono',monospace";
    /* eslint-disable no-console */
    console.log("%cVEYDRAN DIRECTORATE OF SYSTEMS INTEGRITY", head);
    console.log(
      "%cYou are inspecting a record that reports itself as nominal.\n" +
        "The interval 02:18:54 \u2192 02:19:00 is absent from every buffer on this page.\n" +
        "COMMUNICATIONS NOMINAL. NO DISTRESS CONDITION DETECTED.\n" +
        "NO PERSON DETECTED.",
      body
    );
    console.log(
      "%cIf you are reading this, you are exactly the kind of reader this series was written for.",
      body
    );
    /* eslint-enable no-console */
    markEggFound("console");
  }, [isEggActive, markEggFound]);

  // ---- keyboard anomalies ----
  useEffect(() => {
    const konamiArmed = isEggActive("konami");
    const digitsArmed = isEggActive("sixthirteen");
    if (!konamiArmed && !digitsArmed) return undefined;

    const onKey = (e) => {
      if (isTyping(e.target)) return;

      if (konamiArmed) {
        const expected = KONAMI[konamiRef.current];
        if (e.key === expected || e.key.toLowerCase() === expected) {
          konamiRef.current += 1;
          if (konamiRef.current === KONAMI.length) {
            konamiRef.current = 0;
            markEggFound("konami");
            triggerGlitch();
          }
        } else {
          konamiRef.current = e.key === KONAMI[0] ? 1 : 0;
        }
      }

      if (digitsArmed && e.key.length === 1) {
        bufferRef.current = (bufferRef.current + e.key).slice(-6);
        if (/6\.?13$/.test(bufferRef.current)) {
          bufferRef.current = "";
          markEggFound("sixthirteen");
          openOverlay({ kind: "transmission" });
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEggActive, markEggFound, triggerGlitch, openOverlay]);

  return null;
};
